// src/modals/DIRDetailsModal.jsx
import React, { useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import {
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  Paper,
  Tooltip
} from '@mui/material';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Cell,
  ReferenceLine,
  ResponsiveContainer,
  LabelList
} from 'recharts';
import BaseExpandedCard from '../components/cards/BaseExpandedCard';
import ProjectNameLink from '../components/common/ProjectNameLink';
import ClientNameLink from '../components/common/ClientNameLink';
import { LoadingState, ErrorState, EmptyState, StatCard } from '../components/common';
import { useModalData } from '../hooks/useModalData';
import { getDirColor } from '../utils/colorHelpers';
import { formatDirScore, standardizeEmployeeId, formatDate } from '../utils/formatters';
import { API_BASE_URL } from '../config';

// Project-level contributions are small (0.01–0.05); use more decimal places
const fmtImpact = (v) => {
  if (v === null || v === undefined || isNaN(v)) return 'N/A';
  const n = Number(v);
  const str = n.toFixed(2);
  return n > 0 ? `+${str}` : str;
};

const getRatingLabel = (score) => {
  if (score >= 2.0) return { label: 'Exceptional', color: '#03DAC6' };
  if (score >= 0.5) return { label: 'Above Average', color: '#03DAC6' };
  if (score >= -0.5) return { label: 'Average', color: '#FFCF44' };
  return { label: 'Below Average', color: '#CF6679' };
};

const ScoreBar = ({ label, score, tooltip }) => {
  const maxScale = 2;
  const clipped = Math.max(-maxScale, Math.min(maxScale, score ?? 0));
  const pct = Math.abs(clipped) / maxScale * 50;
  const color = getDirColor(clipped);
  return (
    <Box sx={{ mb: 2 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 0.5 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          <Typography sx={{ fontSize: '0.75rem', color: 'text.secondary' }}>{label}</Typography>
          {tooltip && (
            <Tooltip title={tooltip} arrow placement="top">
              <InfoOutlinedIcon sx={{ fontSize: '0.7rem', color: 'rgba(255,255,255,0.3)', cursor: 'help' }} />
            </Tooltip>
          )}
        </Box>
        <Typography sx={{ fontSize: '0.85rem', fontWeight: 700, color }}>{formatDirScore(score ?? 0)}</Typography>
      </Box>
      <Box sx={{ position: 'relative', height: 10, bgcolor: 'rgba(255,255,255,0.06)', borderRadius: 5, overflow: 'hidden' }}>
        <Box sx={{ position: 'absolute', left: '50%', top: 0, bottom: 0, width: 1, bgcolor: 'rgba(255,255,255,0.2)' }} />
        {clipped !== 0 && (
          <Box sx={{
            position: 'absolute', top: 0, bottom: 0,
            ...(clipped > 0 ? { left: '50%', width: `${pct}%` } : { right: '50%', width: `${pct}%` }),
            bgcolor: color, borderRadius: 5
          }} />
        )}
      </Box>
    </Box>
  );
};

const LEGEND_ITEMS = [
  { color: '#03DAC6', label: '+2.0 and above — Exceptional' },
  { color: '#03DAC6', label: '+0.5 to +1.9 — Above Average' },
  { color: '#FFCF44', label: '-0.5 to +0.4 — Average' },
  { color: '#CF6679', label: 'Below -0.5 — Below Average' },
];

const AXIS_TICKS = [-2, -1.75, -1.25, -1, -0.75, -0.5, -0.25, 0, 0.25, 0.5, 0.75, 1, 1.25, 1.5, 1.75, 2];

const DIRDetailsModal = ({ open, onClose, employeeName, employeeId, tabMode = false }) => {
  const [sortField, setSortField] = useState('end_date');
  const [sortDir, setSortDir] = useState('desc');

  const formattedId = useMemo(() => standardizeEmployeeId(employeeId), [employeeId]);

  const { data: dirData, loading, error } = useModalData(
    `${API_BASE_URL}/employees/${formattedId}/dir`,
    open,
    { cache: true, ttl: 5 * 60 * 1000 }
  );

  const dirC = dirData?.normalized_dir?.complete ?? null;
  const dirO = dirData?.normalized_dir?.operational ?? null;
  const rating = getRatingLabel(dirC ?? 0);

  const allProjects = useMemo(() => {
    const { contribution_details, project_details } = dirData || {};
    if (!contribution_details?.project_ids || !project_details) return [];
    const refDate = dirData?.calculation_methodology?.reference_date
      ? new Date(dirData.calculation_methodology.reference_date)
      : new Date();

    return project_details.map((project, i) => ({
      project_id: project.project_id,
      name: project.name,
      client_name: project.client_name,
      end_date: project.end_date,
      complete_impact: contribution_details.complete_contributions?.[i] ?? 0,
      operational_impact: contribution_details.operational_contributions?.[i] ?? 0,
      recency_weight: contribution_details.recency_weights?.[i] ?? 0,
      weightedContribution: (contribution_details.complete_contributions?.[i] ?? 0) * (contribution_details.recency_weights?.[i] ?? 0),
      isActive: project.end_date ? new Date(project.end_date) > refDate : true,
    }));
  }, [dirData]);

  const positiveCount = useMemo(() => allProjects.filter(p => p.weightedContribution > 0).length, [allProjects]);
  const negativeCount = useMemo(() => allProjects.filter(p => p.weightedContribution < 0).length, [allProjects]);
  const strongestImpact = useMemo(() => {
    if (!allProjects.length) return null;
    return allProjects.reduce((max, p) => Math.abs(p.weightedContribution) > Math.abs(max.weightedContribution) ? p : max, allProjects[0]);
  }, [allProjects]);
  const recentCount = useMemo(() => allProjects.filter(p => p.recency_weight >= 0.5).length, [allProjects]);

  const recentFive = useMemo(() => {
    return [...allProjects]
      .sort((a, b) => {
        if (!a.end_date && !b.end_date) return 0;
        if (!a.end_date) return -1;
        if (!b.end_date) return 1;
        return new Date(b.end_date) - new Date(a.end_date);
      })
      .slice(0, 5)
      .map(p => ({
        name: `${p.client_name} — ${p.name}`,
        value: parseFloat(p.weightedContribution.toFixed(2)),
      }));
  }, [allProjects]);

  const sortedProjects = useMemo(() => {
    return [...allProjects].sort((a, b) => {
      let aVal = a[sortField];
      let bVal = b[sortField];
      if (sortField === 'end_date') {
        if (!aVal && !bVal) return 0;
        if (!aVal) return sortDir === 'desc' ? -1 : 1;
        if (!bVal) return sortDir === 'desc' ? 1 : -1;
        aVal = new Date(aVal);
        bVal = new Date(bVal);
      }
      if (aVal < bVal) return sortDir === 'asc' ? -1 : 1;
      if (aVal > bVal) return sortDir === 'asc' ? 1 : -1;
      return 0;
    });
  }, [allProjects, sortField, sortDir]);

  const handleSort = (field) => {
    if (sortField === field) {
      setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDir('desc');
    }
  };

  const title = (
    <>Delivery Impact Rating<sup style={{ fontSize: '0.55em' }}>©</sup>: {employeeName}</>
  );

  const Wrap = ({ children }) => tabMode
    ? <>{children}</>
    : <BaseExpandedCard open={open} onClose={onClose} title={title} subtitle="Delivery impact across all projects">{children}</BaseExpandedCard>;

  if (loading) return <Wrap><LoadingState message="Fetching DIR data..." /></Wrap>;
  if (error) return <Wrap><ErrorState message={error} /></Wrap>;

  return (
    <Wrap>

      {/* Section 1 — Stat Cards */}
      <Box sx={{ display: 'flex', gap: 1.5, mb: 3, flexWrap: 'wrap' }}>
        <StatCard label="Rating">
          <Typography variant="h5" sx={{ fontWeight: 700, color: rating.color, lineHeight: 1 }}>
            {rating.label}
          </Typography>
        </StatCard>

        <StatCard label={
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            DIR<sup style={{ fontSize: '0.55em' }}>©</sup>-C (Complete)
            <Tooltip title="Complete score — combines budget performance, schedule adherence, AND client satisfaction (CSAT). The most holistic view of delivery impact." arrow placement="top">
              <InfoOutlinedIcon sx={{ fontSize: '0.7rem', color: 'rgba(255,255,255,0.3)', cursor: 'help' }} />
            </Tooltip>
          </Box>
        }>
          <Typography variant="h5" sx={{ fontWeight: 700, color: dirC !== null ? getDirColor(dirC) : '#555', lineHeight: 1 }}>
            {dirC !== null ? formatDirScore(dirC) : 'N/A'}
          </Typography>
        </StatCard>

        <StatCard label={
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            DIR<sup style={{ fontSize: '0.55em' }}>©</sup>-O (Operational)
            <Tooltip title="Operational score — measures only budget and schedule performance, excluding client satisfaction. Reflects execution efficiency independent of client feedback." arrow placement="top">
              <InfoOutlinedIcon sx={{ fontSize: '0.7rem', color: 'rgba(255,255,255,0.3)', cursor: 'help' }} />
            </Tooltip>
          </Box>
        }>
          <Typography variant="h5" sx={{ fontWeight: 700, color: dirO !== null ? getDirColor(dirO) : '#555', lineHeight: 1 }}>
            {dirO !== null ? formatDirScore(dirO) : 'N/A'}
          </Typography>
        </StatCard>

        <StatCard label="Projects Included">
          <Typography variant="h5" sx={{ fontWeight: 700, lineHeight: 1 }}>
            {allProjects.length}
          </Typography>
        </StatCard>
      </Box>

      {/* Section 2 — Score Visualization */}
      <Box sx={{ bgcolor: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.07)', borderRadius: 2, p: 2.5, mb: 3 }}>
        <Typography sx={{ fontSize: '0.8rem', fontWeight: 600, mb: 2 }}>Score Visualization</Typography>
        <ScoreBar label="DIR©-C (Complete)" score={dirC} tooltip="Complete score — budget + schedule + client satisfaction. Scores above 0 indicate above-replacement performance." />
        <ScoreBar label="DIR©-O (Operational)" score={dirO} tooltip="Operational score — budget + schedule only, no CSAT. Useful for isolating execution quality from client relationship factors." />
        <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 0.5 }}>
          {AXIS_TICKS.map(t => (
            <Typography key={t} sx={{ fontSize: '0.55rem', color: 'rgba(255,255,255,0.3)', lineHeight: 1 }}>
              {t === 0 ? '0' : (t > 0 ? `+${t}` : t)}
            </Typography>
          ))}
        </Box>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 0.5 }}>
          <Typography sx={{ fontSize: '0.6rem', color: 'rgba(255,255,255,0.3)' }}>Needs Improvement</Typography>
          <Typography sx={{ fontSize: '0.6rem', color: '#FFCF44' }}>Replacement Level</Typography>
          <Typography sx={{ fontSize: '0.6rem', color: 'rgba(255,255,255,0.3)' }}>Exceptional</Typography>
        </Box>
      </Box>

      {/* Section 3 — Score Interpretation Legend */}
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2.5, mb: 3 }}>
        {LEGEND_ITEMS.map(({ color, label }) => (
          <Box key={label} sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
            <Box sx={{ width: 10, height: 10, borderRadius: '50%', bgcolor: color, flexShrink: 0 }} />
            <Typography sx={{ fontSize: '0.7rem', color: 'text.secondary' }}>{label}</Typography>
          </Box>
        ))}
      </Box>

      {/* Section 4 — Project Impact Summary */}
      <Typography sx={{ fontSize: '1rem', fontWeight: 700, mb: 1.5 }}>Project Impact</Typography>
      <Box sx={{ display: 'flex', gap: 1.5, mb: 3, flexWrap: 'wrap' }}>
        <StatCard label="Positive Projects">
          <Typography variant="h5" sx={{ fontWeight: 700, color: '#03DAC6', lineHeight: 1 }}>
            {positiveCount}
          </Typography>
        </StatCard>

        <StatCard label="Negative Projects">
          <Typography variant="h5" sx={{ fontWeight: 700, color: negativeCount > 0 ? '#CF6679' : 'text.primary', lineHeight: 1 }}>
            {negativeCount}
          </Typography>
        </StatCard>

        <StatCard label="Strongest Impact">
          <Typography variant="h5" sx={{ fontWeight: 700, color: strongestImpact ? getDirColor(strongestImpact.weightedContribution) : '#555', lineHeight: 1 }}>
            {strongestImpact ? fmtImpact(strongestImpact.weightedContribution) : 'N/A'}
          </Typography>
        </StatCard>

        <StatCard label={
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            Recent Projects
            <Tooltip title="Projects with a recency weight ≥ 50% (completed within approximately the last year)." arrow placement="top">
              <InfoOutlinedIcon sx={{ fontSize: '0.7rem', color: 'rgba(255,255,255,0.3)', cursor: 'help' }} />
            </Tooltip>
          </Box>
        }>
          <Typography variant="h5" sx={{ fontWeight: 700, lineHeight: 1 }}>
            {recentCount}
          </Typography>
        </StatCard>
      </Box>

      {/* Section 5 — Bar Chart + Peer Comparison side by side */}
      <Box sx={{ display: 'flex', gap: 2, mb: 3, alignItems: 'stretch' }}>

        {/* Bar Chart */}
        {recentFive.length > 0 && (
          <Box sx={{ flex: 2, bgcolor: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.07)', borderRadius: 2, p: 2.5 }}>
            <Typography sx={{ fontSize: '0.8rem', fontWeight: 600, mb: 2 }}>Impact by Project (Most Recent 5)</Typography>
            <ResponsiveContainer width="100%" height={recentFive.length * 44 + 50}>
              <BarChart
                data={recentFive}
                layout="vertical"
                margin={{ top: 0, right: 55, left: 10, bottom: 20 }}
              >
                <XAxis
                  type="number"
                  domain={[-0.15, 0.15]}
                  tickCount={7}
                  tickFormatter={v => fmtImpact(v)}
                  tick={{ fontSize: 10, fill: 'rgba(255,255,255,0.4)' }}
                  axisLine={{ stroke: 'rgba(255,255,255,0.1)' }}
                  tickLine={{ stroke: 'rgba(255,255,255,0.1)' }}
                />
                <YAxis type="category" dataKey="name" width={220} tick={{ fontSize: 11, fill: 'rgba(255,255,255,0.6)' }} />
                <ReferenceLine x={0} stroke="rgba(255,255,255,0.2)" />
                <Bar dataKey="value" radius={[0, 3, 3, 0]}>
                  {recentFive.map((entry, i) => (
                    <Cell key={i} fill={getDirColor(entry.value)} />
                  ))}
                  <LabelList dataKey="value" position="right" formatter={v => fmtImpact(v)} style={{ fontSize: 11, fill: 'rgba(255,255,255,0.8)' }} />
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </Box>
        )}

        {/* Peer Comparison */}
        <Box sx={{ flex: 1, bgcolor: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.07)', borderRadius: 2, p: 2.5 }}>
          <Typography sx={{ fontSize: '0.8rem', fontWeight: 600, mb: 2 }}>Peer Comparison</Typography>
          {[
            { label: 'Company Average', value: dirData?.peer_comparison?.company_average },
            { label: 'Team Average', value: dirData?.peer_comparison?.team_average },
            { label: 'Title Average', value: dirData?.peer_comparison?.title_average },
          ].map(({ label, value }) => (
            <Box key={label} sx={{ mb: 2 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                <Typography sx={{ fontSize: '0.7rem', color: 'text.secondary' }}>{label}</Typography>
                <Typography sx={{ fontSize: '0.8rem', fontWeight: 700, color: value != null ? getDirColor(value) : 'text.secondary' }}>
                  {value != null ? formatDirScore(value) : 'N/A'}
                </Typography>
              </Box>
              <Box sx={{ height: 4, bgcolor: 'rgba(255,255,255,0.06)', borderRadius: 2, overflow: 'hidden' }}>
                {value != null && (
                  <Box sx={{
                    height: '100%', borderRadius: 2,
                    bgcolor: getDirColor(value),
                    width: `${Math.min(Math.abs(value) / 2 * 100, 100)}%`,
                    ml: value < 0 ? 'auto' : 0,
                  }} />
                )}
              </Box>
            </Box>
          ))}

          {/* Percentile */}
          <Box sx={{ mt: 3, pt: 2, borderTop: '1px solid rgba(255,255,255,0.07)' }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
              <Typography sx={{ fontSize: '0.7rem', color: 'text.secondary' }}>Percentile</Typography>
              <Typography sx={{ fontSize: '0.8rem', fontWeight: 700, color: dirData?.peer_comparison?.percentile != null ? getDirColor(dirC ?? 0) : 'text.secondary' }}>
                {dirData?.peer_comparison?.percentile != null ? `${Math.round(dirData.peer_comparison.percentile)}th` : 'N/A'}
              </Typography>
            </Box>
            <Box sx={{ height: 6, bgcolor: 'rgba(255,255,255,0.06)', borderRadius: 3, overflow: 'hidden' }}>
              {dirData?.peer_comparison?.percentile != null && (
                <Box sx={{ height: '100%', borderRadius: 3, bgcolor: getDirColor(dirC ?? 0), width: `${dirData.peer_comparison.percentile}%` }} />
              )}
            </Box>
          </Box>
        </Box>

      </Box>

      {/* Section 6 — All Projects Table */}
      {allProjects.length > 0 ? (
        <Box sx={{ mb: 3 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1.5 }}>
            <Typography sx={{ fontSize: '1rem', fontWeight: 700 }}>All Project Contributions</Typography>
            <Typography sx={{ fontSize: '0.7rem', color: 'text.secondary' }}>{allProjects.length} projects</Typography>
          </Box>
          <TableContainer component={Paper} sx={{ bgcolor: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.07)' }}>
            <Table size="small">
              <TableHead>
                <TableRow sx={{ '& th': { borderColor: 'rgba(255,255,255,0.08)', py: 1 } }}>
                  {[
                    { field: 'project_id', label: 'Project' },
                    { field: 'client_name', label: 'Client' },
                    { field: 'name', label: 'Engagement' },
                    { field: 'end_date', label: 'End Date' },
                    { field: 'complete_impact', label: 'Complete Impact', tooltip: 'How much this project moved the DIR-C score — includes budget, schedule, and client satisfaction, weighted by recency.' },
                    { field: 'operational_impact', label: 'Operational Impact', tooltip: 'How much this project moved the DIR-O score — budget and schedule only, no CSAT, weighted by recency.' },
                    { field: 'recency_weight', label: 'Recency Weight', tooltip: 'Exponential decay factor (half-life 365 days). Recent projects count more. ≥50% = within the last year.' },
                  ].map(({ field, label, tooltip }) => (
                    <TableCell key={field}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        <TableSortLabel
                          active={sortField === field}
                          direction={sortField === field ? sortDir : 'asc'}
                          onClick={() => handleSort(field)}
                          sx={{ fontSize: '0.7rem', color: 'text.secondary', '&.Mui-active': { color: '#BB86FC' } }}
                        >
                          {label}
                        </TableSortLabel>
                        {tooltip && (
                          <Tooltip title={tooltip} arrow placement="top">
                            <InfoOutlinedIcon sx={{ fontSize: '0.7rem', color: 'rgba(255,255,255,0.25)', cursor: 'help', flexShrink: 0 }} />
                          </Tooltip>
                        )}
                      </Box>
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {sortedProjects.map((p, i) => (
                  <TableRow key={`${p.project_id}-${i}`} sx={{ '&:hover': { bgcolor: 'rgba(255,255,255,0.03)' }, '& td': { borderColor: 'rgba(255,255,255,0.05)', py: 0.75 } }}>
                    <TableCell sx={{ fontSize: '0.75rem', color: '#64B5F6' }}>{p.project_id}</TableCell>
                    <TableCell sx={{ fontSize: '0.75rem' }}>
                      <ClientNameLink client={{ client_name: p.client_name }}>{p.client_name}</ClientNameLink>
                    </TableCell>
                    <TableCell sx={{ fontSize: '0.75rem', color: 'text.secondary' }}>
                      <ProjectNameLink project={{ project_id: p.project_id, name: p.name, client_name: p.client_name }}>{p.name}</ProjectNameLink>
                    </TableCell>
                    <TableCell sx={{ fontSize: '0.75rem', color: 'text.secondary' }}>{p.end_date ? formatDate(p.end_date) : 'Ongoing'}</TableCell>
                    <TableCell sx={{ fontSize: '0.75rem', fontWeight: 700, color: getDirColor(p.complete_impact) }}>{fmtImpact(p.complete_impact)}</TableCell>
                    <TableCell sx={{ fontSize: '0.75rem', fontWeight: 700, color: getDirColor(p.operational_impact) }}>{fmtImpact(p.operational_impact)}</TableCell>
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Box sx={{ width: 40, height: 4, bgcolor: 'rgba(255,255,255,0.1)', borderRadius: 2, overflow: 'hidden' }}>
                          <Box sx={{ width: `${Math.min(p.recency_weight * 100, 100)}%`, height: '100%', bgcolor: '#BB86FC', borderRadius: 2 }} />
                        </Box>
                        <Typography sx={{ fontSize: '0.7rem', color: 'text.secondary' }}>{(p.recency_weight * 100).toFixed(0)}%</Typography>
                      </Box>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Box>
      ) : (
        <EmptyState title="No contributing projects" message="No project contribution data found for this employee" />
      )}

      {/* Footer */}
      <Typography sx={{ fontSize: '0.65rem', color: 'rgba(255,255,255,0.3)', mt: 2, lineHeight: 1.6 }}>
        DIR 2.1 — Measures delivery impact relative to a 25th percentile "replacement level" baseline. A score of 0.0 means performing at replacement level, not zero contribution. Recent projects weighted more heavily (365-day exponential decay half-life). Each project's contribution shows how much it moved the DIR score above or below the baseline, factored by recency weight. Positive values indicate above-replacement performance on that engagement.
      </Typography>
    </Wrap>
  );
};

DIRDetailsModal.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  employeeName: PropTypes.string.isRequired,
  employeeId: PropTypes.string.isRequired
};

export default DIRDetailsModal;
